import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Plus, Minus, ShoppingBag, Send, BookOpen, Search } from 'lucide-react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { formatMoney } from '@/utils/currency';
import Concierge from '@/components/Concierge/Concierge';
import GroupTabBar from '@/components/GroupTab/GroupTabBar';
import { UiStyles } from '@/theme/ui';

interface Props {
  branchId: string;
  /** The seated table session. Absent in viewOnly (browsing before booking). */
  bookingId?: string;
  tableName?: string;
  onClose: () => void;
  /** Opens the static picture menu (MenuImagesModal) — the "browse before you order" view. */
  onViewMenu?: () => void;
  /** Browse-only: show the real inventory menu (items, prices, descriptions, photos) but no
   *  add-to-cart / send. Used by registered customers viewing a branch before they're seated. */
  viewOnly?: boolean;
  /** viewOnly footer CTA — take the customer to the book-a-table flow. */
  onBook?: () => void;
}

interface ServingSize { id: string; label: string; volumeMl?: number | null; price: number; sortOrder?: number }
interface CatalogItem { id: string; itemName: string; itemImage?: string; itemPrice?: number; itemDescription?: string; available?: boolean; subCategory?: string | null; servingSizes?: ServingSize[] | null; foodType?: string | null; spiceLevel?: number | null; allergens?: string[] | null; dietaryTags?: string[] | null; course?: string | null; isCombo?: boolean; comboItems?: Array<{ stockItemId: string; quantity: number }> | null }

// Restaurant menu badges (veg/non-veg dot, spice, dietary tags, allergen note). Null-safe: renders nothing on bar items.
const DOT_COLOR: Record<string, string> = { veg: '#16a34a', non_veg: '#dc2626', egg: '#d97706' };
const DIET_LABEL: Record<string, string> = { vegan: 'Vegan', gluten_free: 'GF', halal: 'Halal', jain: 'Jain', contains_alcohol: '21+' };
const dietPill: React.CSSProperties = { fontSize: '0.62rem', fontWeight: 800, color: '#0f766e', background: '#ccfbf1', borderRadius: 9999, padding: '1px 7px', letterSpacing: '.02em' };
function FoodBadges({ it }: { it: CatalogItem }) {
  const hasAny = it.foodType || it.spiceLevel || (it.dietaryTags && it.dietaryTags.length) || (it.allergens && it.allergens.length);
  if (!hasAny) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, margin: '3px 0 1px' }}>
      {it.foodType && (
        <span title={it.foodType.replace('_', '-')} style={{ width: 13, height: 13, borderRadius: 3, border: `2px solid ${DOT_COLOR[it.foodType] || '#94a3b8'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: DOT_COLOR[it.foodType] || '#94a3b8' }} />
        </span>
      )}
      {!!it.spiceLevel && <span style={{ fontSize: '0.7rem' }} title={`Spice ${it.spiceLevel}/3`}>{'🌶'.repeat(it.spiceLevel)}</span>}
      {(it.dietaryTags || []).map((d) => <span key={d} style={dietPill}>{DIET_LABEL[d] || d}</span>)}
      {it.allergens && it.allergens.length > 0 && <span style={{ fontSize: '0.66rem', color: '#b45309' }}>⚠ {it.allergens.join(', ')}</span>}
    </div>
  );
}
interface CatalogGroup { categoryName: string; items: CatalogItem[] }
interface CartLine { key: string; stockItemId?: string | null; itemName: string; itemImage?: string | null; quantity: number; isManual: boolean; unitPrice?: number | null; servingMl?: number | null; isComplimentary?: boolean }
interface OrderItem { id: string; itemName: string; quantity: number; isManual: boolean; stockItemId?: string | null; itemImage?: string | null; unitPrice?: number | string | null }
interface Order { id: string; status: string; tableLabel?: string | null; items: OrderItem[]; createdAt: string }

const OPEN_STATUSES = ['pending', 'approved', 'making', 'coming'];
const fmtDate = (d: string) => { try { return new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }); } catch { return d; } };
const orderTotal = (o: Order) => (o.items || []).reduce((s, i) => s + (Number(i.unitPrice) || 0) * (i.quantity || 0), 0);

const STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  pending: { label: 'Waiting for approval', bg: '#fef3c7', fg: '#92400e' },
  approved: { label: 'Approved', bg: '#dbeafe', fg: '#1e40af' },
  making: { label: 'Making', bg: '#e0e7ff', fg: '#3730a3' },
  coming: { label: 'On the way', bg: '#ccfbf1', fg: '#0f766e' },
  delivered: { label: 'Delivered', bg: '#dcfce7', fg: '#166534' },
  rejected: { label: 'Declined', bg: '#fee2e2', fg: '#b91c1c' },
  cancelled: { label: 'Cancelled', bg: '#f3f4f6', fg: '#6b7280' },
};

/**
 * Customer "order items" panel, opened from the check-in chat. Browse the branch catalogue + add
 * manual items ("Wine"), send an order to the organizer, and watch it move Approved → Making →
 * On the way → Delivered live.
 */
const TableServiceOrderModal: React.FC<Props> = ({ branchId, bookingId, tableName, onClose, onViewMenu, viewOnly = false, onBook }) => {
  const [groups, setGroups] = useState<CatalogGroup[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [cart, setCart] = useState<Record<string, CartLine>>({});
  const [search, setSearch] = useState('');
  const [vegOnly, setVegOnly] = useState(false); // restaurant: hide non-veg/egg items
  const [orderType, setOrderType] = useState<'dine_in' | 'takeaway'>('dine_in');
  const [manualName, setManualName] = useState('');
  const [manualQty, setManualQty] = useState(1);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  // Which tab is showing: place a New order, track the Current (in-session) orders, or Previous visits.
  const [tab, setTab] = useState<'new' | 'current' | 'previous'>('new');
  // Item tapped to zoom in / view details (photo + full description).
  const [zoomItem, setZoomItem] = useState<{ item: CatalogItem; category: string } | null>(null);
  const [history, setHistory] = useState<Order[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [entitlement, setEntitlement] = useState<{ granted: number; remaining: number; eligibleStarters: Array<{ id: string; name: string; image: string | null; price: number | null }> } | null>(null);
  useEffect(() => {
    if (!branchId || !bookingId) { setEntitlement(null); return; }
    let alive = true;
    apiService.starterEntitlement(branchId, bookingId).then((e) => { if (alive) setEntitlement(e); }).catch(() => { if (alive) setEntitlement(null); });
    return () => { alive = false; };
  }, [branchId, bookingId, orders.length]);
  const manualCounter = useRef(0);
  // Sticky category chips (delivery-app style): scroll container + per-category anchors + active chip.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const chipBarRef = useRef<HTMLDivElement | null>(null);
  const catRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeCat, setActiveCat] = useState<string | null>(null);

  // Format money in the branch's currency (falls back to INR inside formatMoney).
  const money = (n: number) => formatMoney(n, currency);

  const myUserId = useMemo(() => {
    const { accessToken } = apiService.getTokens();
    return accessToken ? getUserIdFromToken(accessToken) : null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [details, mine] = await Promise.all([
          apiService.getBranchDetails(branchId).catch(() => null),
          apiService.getServiceOrders().catch(() => ({ data: [] as Order[] })),
        ]);
        if (cancelled) return;
        const cur = (details?.data as any)?.currency || (details?.data as any)?.branch?.currency;
        if (cur) setCurrency(cur);
        const cats = (details?.data?.stockItemsByCategory || []) as any[];
        const g: CatalogGroup[] = cats.map((c) => {
          const items: CatalogItem[] = [];
          // Un-subcategorised items first, then each subcategory's items tagged with its name so the
          // customer sees sections like Beer → Bucket → items.
          (c.itemsWithoutSubCategory || []).forEach((it: any) => items.push({ ...it, subCategory: null }));
          (c.subCategories || []).forEach((sc: any) => {
            const scName = sc.subCategory?.subCategoryName || null;
            (sc.items || []).forEach((it: any) => items.push({ ...it, subCategory: scName }));
          });
          return { categoryName: c.category?.categoryName || 'Items', items };
        }).filter((x) => x.items.length > 0);
        setGroups(g);
        setOrders(((mine as any)?.data || []) as Order[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load items');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [branchId]);

  // Live order status updates.
  useEffect(() => {
    if (!myUserId) return;
    const unsub = subscribeToChannel(
      `user:${myUserId}`,
      ['service_order:updated', 'service_order:new'] as unknown as readonly string[],
      (_event, data) => {
        if (!data?.id) return;
        setOrders((prev) => {
          const i = prev.findIndex((o) => o.id === data.id);
          if (i === -1) return [data as Order, ...prev];
          const next = prev.slice(); next[i] = data as Order; return next;
        });
      }
    );
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [myUserId]);

  // Poll fallback: while any order is still OPEN (pending/approved/making/coming), re-fetch orders
  // every 5s in case the Ably `service_order:updated` event was missed (dropped/misconfigured
  // connection). Mirrors the CallStaff status poll. Stops automatically once no order is open and
  // on unmount (modal close). Merges by id — updates existing orders, prepends brand-new ones — so
  // replays never duplicate. This is additive: the Ably subscription above is untouched.
  const hasOpenOrder = orders.some((o) => OPEN_STATUSES.includes(o.status));
  useEffect(() => {
    if (!hasOpenOrder) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiService.getServiceOrders();
        if (cancelled) return;
        const fetched = (((res as any)?.data) || []) as Order[];
        if (!fetched.length) return;
        setOrders((prev) => {
          const next = prev.map((o) => fetched.find((f) => f?.id === o.id) || o);
          const newOnes = fetched.filter((f) => f?.id && !prev.some((p) => p.id === f.id));
          return newOnes.length ? [...newOnes, ...next] : next;
        });
      } catch { /* non-fatal: chat/history still work over REST */ }
    };
    const iv = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [hasOpenOrder]);

  const addCatalog = (it: CatalogItem) => setCart((prev) => ({
    ...prev,
    [it.id]: { key: it.id, stockItemId: it.id, itemName: it.itemName, itemImage: it.itemImage, quantity: (prev[it.id]?.quantity || 0) + 1, isManual: false, unitPrice: typeof it.itemPrice === 'number' ? it.itemPrice : null },
  }));
  const decCatalog = (id: string) => setCart((prev) => {
    const cur = prev[id];
    if (!cur) return prev;
    const q = cur.quantity - 1;
    const next = { ...prev };
    if (q <= 0) delete next[id]; else next[id] = { ...cur, quantity: q };
    return next;
  });
  // Serving-size variants (a specific pour of an item) → a distinct cart line keyed by item+size.
  const addCatalogSize = (it: CatalogItem, size: ServingSize) => setCart((prev) => {
    const key = `${it.id}::${size.id}`;
    return { ...prev, [key]: { key, stockItemId: it.id, itemName: `${it.itemName} · ${size.label}`, itemImage: it.itemImage, quantity: (prev[key]?.quantity || 0) + 1, isManual: false, unitPrice: size.price, servingMl: size.volumeMl ?? null } };
  });
  const decCatalogSize = (it: CatalogItem, sizeId: string) => setCart((prev) => {
    const key = `${it.id}::${sizeId}`;
    const cur = prev[key];
    if (!cur) return prev;
    const q = cur.quantity - 1;
    const next = { ...prev };
    if (q <= 0) delete next[key]; else next[key] = { ...cur, quantity: q };
    return next;
  });
  const sizeQtyTotal = (it: CatalogItem) => (it.servingSizes || []).reduce((s, sz) => s + (cart[`${it.id}::${sz.id}`]?.quantity || 0), 0);
  const addManual = () => {
    const name = manualName.trim();
    if (!name) return;
    const key = `manual-${manualCounter.current++}`;
    setCart((prev) => ({ ...prev, [key]: { key, stockItemId: null, itemName: name, quantity: Math.max(1, manualQty), isManual: true } }));
    setManualName(''); setManualQty(1);
  };
  const removeLine = (key: string) => setCart((prev) => { const n = { ...prev }; delete n[key]; return n; });

  const lines = Object.values(cart);
  const totalItems = lines.reduce((s, l) => s + l.quantity, 0);
  const cartTotal = lines.reduce((s, l) => s + (l.isComplimentary ? 0 : (Number(l.unitPrice) || 0)) * l.quantity, 0);
  // Free-starter allowance for this bill (e.g. a whisky bottle grants 2 free starters).
  const compInCart = lines.filter((l) => l.isComplimentary).reduce((s, l) => s + l.quantity, 0);
  const freeRemaining = Math.max(0, (entitlement?.remaining || 0) - compInCart);
  const addFreeStarter = (st: { id: string; name: string; image: string | null }) => {
    if (freeRemaining <= 0) return;
    const key = `free-${st.id}`;
    setCart((prev) => {
      const ex = prev[key];
      return { ...prev, [key]: ex ? { ...ex, quantity: ex.quantity + 1 } : { key, stockItemId: st.id, itemName: st.name, itemImage: st.image, quantity: 1, isManual: false, unitPrice: 0, isComplimentary: true } };
    });
  };
  // Running tab = total of orders still open (not delivered/cancelled/declined) for this table.
  const runningTab = orders.filter((o) => OPEN_STATUSES.includes(o.status)).reduce((s, o) => s + orderTotal(o), 0);

  // One-tap "Order again": drop a past order's items back into the cart to review + send.
  const reorder = (o: Order) => {
    setCart((prev) => {
      const next = { ...prev };
      (o.items || []).forEach((it, idx) => {
        if (it.stockItemId) {
          const k = it.stockItemId;
          next[k] = { key: k, stockItemId: k, itemName: it.itemName, itemImage: it.itemImage, quantity: (next[k]?.quantity || 0) + (it.quantity || 1), isManual: false, unitPrice: it.unitPrice != null ? Number(it.unitPrice) : null };
        } else {
          const k = `manual-${manualCounter.current++}-${idx}`;
          next[k] = { key: k, stockItemId: null, itemName: it.itemName, quantity: it.quantity || 1, isManual: true, unitPrice: it.unitPrice != null ? Number(it.unitPrice) : null };
        }
      });
      return next;
    });
    setError(null);
    setTab('new'); // jump to New order so they see the cart + Send
  };

  // Edit a still-pending order: load its items into the cart and switch Send → Update.
  const startEdit = (o: Order) => {
    const next: Record<string, CartLine> = {};
    (o.items || []).forEach((it, idx) => {
      if (it.stockItemId) {
        next[it.stockItemId] = { key: it.stockItemId, stockItemId: it.stockItemId, itemName: it.itemName, itemImage: it.itemImage, quantity: it.quantity || 1, isManual: false, unitPrice: it.unitPrice != null ? Number(it.unitPrice) : null };
      } else {
        const k = `manual-${manualCounter.current++}-${idx}`;
        next[k] = { key: k, stockItemId: null, itemName: it.itemName, quantity: it.quantity || 1, isManual: true, unitPrice: it.unitPrice != null ? Number(it.unitPrice) : null };
      }
    });
    setCart(next);
    setNote((o as any).note || '');
    setEditingOrderId(o.id);
    setError(null);
    setTab('new'); // editing happens in the New order tab (cart + Update)
  };
  const cancelEdit = () => { setEditingOrderId(null); setCart({}); setNote(''); };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try { const res = await apiService.getServiceOrderHistory(); setHistory(((res as any)?.data || []) as Order[]); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load history'); }
    finally { setHistoryLoading(false); }
  };
  // Switch to a tab; lazy-load history the first time Previous is opened.
  const goTab = (t: 'new' | 'current' | 'previous') => {
    setTab(t);
    if (t === 'previous' && history.length === 0) void loadHistory();
  };

  // Show the veg-only toggle only when this menu actually tags food type (i.e. a food venue).
  const hasFoodTypes = useMemo(() => groups.some((g) => g.items.some((i) => i.foodType)), [groups]);
  // Resolve combo component ids to names from the loaded menu, so a combo card can show what's inside.
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) for (const i of g.items) m.set(i.id, i.itemName);
    return m;
  }, [groups]);
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    let gs = groups;
    if (vegOnly) gs = gs.map((g) => ({ ...g, items: g.items.filter((i) => i.foodType !== 'non_veg' && i.foodType !== 'egg') })).filter((g) => g.items.length > 0);
    if (q) gs = gs.map((g) => ({ ...g, items: g.items.filter((i) => i.itemName.toLowerCase().includes(q)) })).filter((g) => g.items.length > 0);
    return gs;
  }, [groups, search, vegOnly]);

  // Tap a category chip → smooth-scroll to that section (accounting for the sticky bar height).
  const scrollToCat = (name: string) => {
    const el = catRefs.current[name];
    const container = bodyRef.current;
    if (!el || !container) return;
    container.scrollTo({ top: Math.max(0, el.offsetTop), behavior: 'smooth' });
    setActiveCat(name);
  };
  // Scroll-spy: highlight the chip for the category you're currently scrolled into.
  useEffect(() => {
    const container = bodyRef.current;
    if (!container || tab !== 'new') return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const off = 10;
        const st = container.scrollTop + off;
        let cur: string | null = null;
        for (const g of filteredGroups) {
          const el = catRefs.current[g.categoryName];
          if (el && el.offsetTop <= st) cur = g.categoryName;
        }
        setActiveCat(cur ?? (filteredGroups[0]?.categoryName ?? null));
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { container.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [filteredGroups, tab]);

  const send = async () => {
    if (!bookingId || lines.length === 0 || sending) return;
    setSending(true); setError(null);
    const items = lines.map((l) => ({ stockItemId: l.stockItemId, itemName: l.itemName, itemImage: l.itemImage, quantity: l.quantity, isManual: l.isManual, unitPrice: l.isComplimentary ? 0 : (l.unitPrice ?? null), servingMl: l.servingMl ?? null, isComplimentary: !!l.isComplimentary }));
    try {
      if (editingOrderId) {
        const res = await apiService.editServiceOrder(editingOrderId, { items, note: note.trim() });
        const order = (res as any)?.data;
        if (order?.id) setOrders((prev) => prev.map((o) => (o.id === order.id ? order : o)));
        setEditingOrderId(null);
      } else {
        const res = await apiService.createServiceOrder({ bookingId, items, note: note.trim() || undefined, orderType });
        const order = (res as any)?.data;
        if (order?.id) setOrders((prev) => [order as Order, ...prev]);
      }
      setCart({}); setNote('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send order');
    } finally {
      setSending(false);
    }
  };

  const cancelOrder = async (id: string) => {
    try {
      const res = await apiService.cancelServiceOrder(id);
      const order = (res as any)?.data;
      if (order?.id) setOrders((prev) => prev.map((o) => (o.id === order.id ? order : o)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel');
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div className="ui-scope" style={panel} onClick={(e) => e.stopPropagation()}>
        <UiStyles />
        <div style={sheetGrip} aria-hidden><span style={sheetGripBar} /></div>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <ShoppingBag size={18} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>{viewOnly ? 'Menu' : 'Order to your table'}</div>
              <div style={subLabel}>{viewOnly ? 'Browse the menu — book a table to order' : (tableName || 'Browse items and send your order')}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {onViewMenu && (
              <button type="button" onClick={onViewMenu} style={viewMenuBtn} title="Photos of the printed menu">
                <BookOpen size={15} /> Menu Images
              </button>
            )}
            <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
          </div>
        </div>

        {/* Tabs — New order · Current · Previous, left to right. Hidden when just browsing the menu. */}
        {!viewOnly && (
          <div style={tabBar}>
            <button style={tabStyle(tab === 'new')} onClick={() => goTab('new')}>
              🆕 New order{totalItems > 0 ? ` · ${totalItems}` : ''}
            </button>
            <button style={tabStyle(tab === 'current')} onClick={() => goTab('current')}>
              🧾 Current{orders.length > 0 ? ` · ${orders.length}` : ''}
            </button>
            <button style={tabStyle(tab === 'previous')} onClick={() => goTab('previous')}>
              🕘 Previous{history.length > 0 ? ` · ${history.length}` : ''}
            </button>
          </div>
        )}

        {/* Persistent search — always visible under the tabs, searches all categories by name. */}
        {tab === 'new' && !loading && groups.length > 0 && (
          <div style={searchBar}>
            <Search size={16} color="#9ca3af" />
            <input style={searchBarInput} placeholder="Search menu…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button type="button" style={searchClear} onClick={() => setSearch('')} aria-label="Clear search"><X size={15} /></button>}
            {hasFoodTypes && (
              <button type="button" onClick={() => setVegOnly((v) => !v)} aria-pressed={vegOnly}
                title="Show vegetarian items only"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: '0 0 auto', padding: '5px 10px', borderRadius: 9999, border: `1.5px solid ${vegOnly ? '#16a34a' : '#e5e7eb'}`, background: vegOnly ? '#dcfce7' : '#fff', color: vegOnly ? '#15803d' : '#6b7280', fontWeight: 800, fontSize: '0.74rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, border: '2px solid #16a34a', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#16a34a' }} />
                </span>
                Veg
              </button>
            )}
          </div>
        )}

        {/* Category chips — a fixed strip right under the tabs (never scrolls, so nothing peeks above it). */}
        {tab === 'new' && !loading && !search && filteredGroups.length > 1 && (
          <div ref={chipBarRef} style={catChipBar}>
            {filteredGroups.map((g) => (
              <button key={g.categoryName} type="button" style={activeCat === g.categoryName ? catChipActive : catChip} onClick={() => scrollToCat(g.categoryName)}>
                {g.categoryName}
              </button>
            ))}
          </div>
        )}

        <div ref={bodyRef} style={{ ...body, position: 'relative' }}>
          {/* Scroll-away top spacer: keeps breathing room at rest, but lets the pinned
              section header sit flush under the chip strip once you scroll. */}
          <div style={{ height: 12 }} aria-hidden />
          {/* ─────────── NEW ORDER ─────────── */}
          {tab === 'new' && (
            <>
              {/* Group tab / shared cart — opt-in; private by default. */}
              {bookingId && <GroupTabBar bookingId={bookingId} currency={currency} />}
              {/* One AI, menu doorway: the SAME Concierge (one server-side thread) framed for the menu —
                  recommendations, pairings, ordering. Avoids a second, separate "AI waiter" brain. */}
              {bookingId && <Concierge bookingId={bookingId} branchId={branchId} launchTitle="✨ Ask about the menu" launchSub="Recommendations, pairings, what's veg…" />}
              {/* Free starters granted by the bill (e.g. a whisky bottle → 2 free starters). */}
              {entitlement && entitlement.granted > 0 && (
                <div style={{ margin: '4px 0 12px', padding: 12, borderRadius: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)' }}>
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>🎁 Free starters {freeRemaining > 0 ? `· ${freeRemaining} left` : '· all added'}</div>
                  {entitlement.eligibleStarters.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Your free starters will appear here.</div>
                  ) : freeRemaining <= 0 ? (
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>You’ve added all your free starters. 🎉</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {entitlement.eligibleStarters.map((st) => (
                        <button key={st.id} type="button" onClick={() => addFreeStarter(st)}
                          style={{ border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.15)', color: 'inherit', borderRadius: 999, padding: '6px 14px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
                          + {st.name} · FREE
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Ready-made items straight off the branch menu. */}
              <div style={sectionTitle}>🍔 From the menu</div>
              <div style={sectionHint}>Ready-made items — tap “Add”.</div>
              {loading ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Loading items…</p>
              ) : filteredGroups.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '2px' }}>{search ? 'No menu items match your search.' : 'No menu items yet — add your own below.'}</p>
              ) : (
                <>
                  <div style={menuListWrap}>
                    {filteredGroups.map((g, gi) => (
                      <div key={g.categoryName} ref={(el) => { catRefs.current[g.categoryName] = el; }}>
                        <div style={catHeadBig}>
                          <span style={catEmoji} aria-hidden>{menuFallback(g.categoryName, g.categoryName).emoji}</span>
                          <span style={catTitle}>{g.categoryName}</span>
                          <span style={catCount}>{g.items.length} {g.items.length === 1 ? 'item' : 'items'}</span>
                        </div>
                        {(() => { let lastSub: string | null | undefined = undefined; return g.items.map((it, idx) => {
                          const sub = it.subCategory ?? null;
                          const showSub = !!sub && sub !== lastSub;
                          lastSub = sub;
                          const q = cart[it.id]?.quantity || 0;
                          const soldOut = it.available === false;
                          const isLastRow = gi === filteredGroups.length - 1 && idx === g.items.length - 1;
                          const sizes = it.servingSizes && it.servingSizes.length ? [...it.servingSizes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)) : null;
                          const sizeTotal = sizes ? sizeQtyTotal(it) : 0;
                          const minSizePrice = sizes ? Math.min(...sizes.map((s) => s.price)) : undefined;
                          return (
                            <React.Fragment key={it.id}>
                              {showSub && <div style={subHead}>{sub}</div>}
                              <div style={isLastRow ? { ...dRow, borderBottom: 'none' } : dRow}>
                                {/* Left: name · description · price */}
                                <div style={dInfo}>
                                  <div style={soldOut ? { ...dName, color: '#9ca3af' } : dName}>
                                    {it.itemName}
                                    {it.isCombo ? <span style={comboBadge}>COMBO</span> : null}
                                  </div>
                                  <FoodBadges it={it} />
                                  {it.isCombo && it.comboItems?.length
                                    ? <div style={comboInside}>{it.comboItems.map((ci) => `${ci.quantity > 1 ? ci.quantity + '× ' : ''}${nameById.get(ci.stockItemId) || 'item'}`).join(' + ')}</div>
                                    : null}
                                  {it.itemDescription ? <div style={dDesc}>{it.itemDescription}</div> : null}
                                  <div style={dPriceRow}>
                                    {sizes ? (
                                      <span style={dPrice}>From {money(minSizePrice!)}</span>
                                    ) : (typeof it.itemPrice === 'number' && <span style={dPrice}>{money(it.itemPrice)}</span>)}
                                    {sizes && <span style={dSizeTag}>{sizes.length} sizes</span>}
                                    {soldOut && <span style={dSoldTag}>Sold out</span>}
                                  </div>
                                </div>
                                {/* Right: image (tap to open) with a floating Add / stepper / size chooser */}
                                <div style={dMedia}>
                                  <button type="button" style={dImgBtn} onClick={() => setZoomItem({ item: it, category: g.categoryName })} aria-label={`View ${it.itemName}`}>
                                    <div style={soldOut ? { opacity: 0.5, lineHeight: 0 } : { lineHeight: 0 }}>
                                      <MenuThumb src={it.itemImage} name={it.itemName} category={g.categoryName} row />
                                    </div>
                                  </button>
                                  {!soldOut && !viewOnly && (
                                    <div style={dAddWrap}>
                                      {sizes ? (
                                        <button type="button" style={dAddFloat} onClick={() => setZoomItem({ item: it, category: g.categoryName })}>{sizeTotal > 0 ? `${sizeTotal} · Edit` : 'Choose'}</button>
                                      ) : q > 0 ? (
                                        <div style={dStepFloat}>
                                          <button type="button" style={dStepBtn} onClick={() => decCatalog(it.id)} aria-label="Remove one"><Minus size={15} /></button>
                                          <span style={dStepQty}>{q}</span>
                                          <button type="button" style={dStepBtn} onClick={() => addCatalog(it)} aria-label="Add one"><Plus size={15} /></button>
                                        </div>
                                      ) : (
                                        <button type="button" style={dAddFloat} onClick={() => addCatalog(it)}>Add</button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        }); })()}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Custom items — typed by the guest, not on the menu. Visually set apart. Ordering only. */}
              {!viewOnly && <>
              <div style={{ ...sectionTitle, marginTop: 20 }}>✍️ Add your own</div>
              <div style={sectionHint}>Not on the menu? Type it — staff will confirm the price.</div>
              <div style={customBox}>
                <div style={manualRow}>
                  <input style={{ ...searchInput, margin: 0, flex: 1 }} placeholder="e.g. Wine, Birthday cake" value={manualName} onChange={(e) => setManualName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addManual(); }} />
                  <input style={qtyInput} type="number" min={1} value={manualQty} onChange={(e) => setManualQty(parseInt(e.target.value, 10) || 1)} />
                  <button style={addManualBtn} onClick={addManual}><Plus size={16} /></button>
                </div>
                {lines.filter((l) => l.isManual).map((l) => (
                  <div key={l.key} style={customLine}>
                    <span style={customTag}>Custom</span>
                    <span style={{ flex: 1, fontSize: '0.92rem' }}>{l.quantity}× {l.itemName}</span>
                    <button style={{ ...addBtn, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }} onClick={() => removeLine(l.key)}>Remove</button>
                  </div>
                ))}
              </div>
              </>}
            </>
          )}

          {/* ─────────── CURRENT ORDERS ─────────── */}
          {tab === 'current' && (
            orders.length === 0 ? (
              <div style={emptyState}>No active orders yet.<br /><span style={{ fontSize: '0.82rem' }}>Place one from the New order tab.</span></div>
            ) : (
              <>
                {runningTab > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <span style={tabPill} title="Running total for orders in progress">Tab {money(runningTab)}</span>
                  </div>
                )}
                {orders.map((o) => {
                  const s = STATUS[o.status] || STATUS.pending;
                  const t = orderTotal(o);
                  return (
                    <div key={o.id} style={orderCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {(o.items || []).map((i) => `${i.quantity}× ${i.itemName}`).join(', ')}
                        </span>
                        <span style={{ ...statusBadge, background: s.bg, color: s.fg }}>{s.label}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {o.status === 'pending' && (
                            <button style={editBtn} onClick={() => startEdit(o)}>✎ Edit</button>
                          )}
                          {o.status === 'pending' && (
                            <button style={cancelBtn} onClick={() => cancelOrder(o.id)}>Cancel</button>
                          )}
                          <button style={reorderBtn} onClick={() => reorder(o)}>↻ Order again</button>
                        </div>
                        {t > 0 && <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>{money(t)}</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            )
          )}

          {/* ─────────── PREVIOUS ORDERS / VISITS ─────────── */}
          {tab === 'previous' && (
            historyLoading ? <p style={{ textAlign: 'center', color: '#6b7280', padding: '0.5rem', fontSize: '0.85rem' }}>Loading…</p>
            : history.length === 0 ? <div style={emptyState}>No past orders yet.</div>
            : (
              <div>
                {history.map((o) => {
                  const s = STATUS[o.status] || STATUS.pending;
                  const t = orderTotal(o);
                  return (
                    <div key={o.id} style={{ ...orderCard, background: '#fbfbfc' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{(o as any).branchName ? `${(o as any).branchName} · ` : ''}{fmtDate(o.createdAt)}</span>
                        <span style={{ ...statusBadge, background: s.bg, color: s.fg }}>{s.label}</span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#374151', marginTop: 4 }}>{(o.items || []).map((i) => `${i.quantity}× ${i.itemName}`).join(', ')}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                        <button style={reorderBtn} onClick={() => reorder(o)}>↻ Order again</button>
                        {t > 0 && <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{money(t)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>

        {tab === 'new' && editingOrderId && (
          <div style={editBanner}>
            ✎ Editing your pending order — change items, then Update.
            <button style={cancelEditBtn} onClick={cancelEdit}>Cancel edit</button>
          </div>
        )}
        {error && <div style={errorBox}>{error}</div>}

        {/* Dine-in vs takeaway — shown on food venues (menu tags food type). */}
        {tab === 'new' && !viewOnly && hasFoodTypes && !editingOrderId && (
          <div style={{ display: 'flex', gap: 8, padding: '8px 12px 0' }}>
            {([['dine_in', '🍽 Dine-in'], ['takeaway', '🥡 Takeaway']] as const).map(([v, label]) => (
              <button key={v} type="button" onClick={() => setOrderType(v)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 10, border: `1.5px solid ${orderType === v ? '#b45309' : '#e5e7eb'}`, background: orderType === v ? '#fff7ed' : '#fff', color: orderType === v ? '#b45309' : '#6b7280', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer' }}>{label}</button>
            ))}
          </div>
        )}

        {tab === 'new' && !viewOnly && (
          <div style={footer}>
            <input style={{ ...searchInput, margin: 0, flex: 1 }} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button style={{ ...sendBtn, opacity: totalItems > 0 && !sending ? 1 : 0.5, ...(editingOrderId ? { background: '#2563eb' } : {}) }} disabled={totalItems === 0 || sending} onClick={send}>
              <Send size={16} /> {sending ? (editingOrderId ? 'Updating…' : 'Sending…') : editingOrderId ? 'Update order' : `Send${totalItems ? ` · ${cartTotal > 0 ? money(cartTotal) : totalItems}` : ''}`}
            </button>
          </div>
        )}
        {viewOnly && (
          <div style={footer}>
            <button style={{ ...sendBtn, flex: 1, justifyContent: 'center' }} onClick={() => { onClose(); onBook?.(); }}>
              Book a table to order
            </button>
          </div>
        )}
      </div>

      {/* Tap-to-zoom item detail: large photo (or category tile) + full description + Add. */}
      {zoomItem && (
        <div className="ui-scope" style={zoomOverlay} onClick={() => setZoomItem(null)}>
          <div style={zoomCard} onClick={(e) => e.stopPropagation()}>
            <button type="button" style={zoomClose} onClick={() => setZoomItem(null)} aria-label="Close"><X size={20} /></button>
            <div style={zoomImgWrap}>
              {zoomItem.item.itemImage ? (
                <img src={zoomItem.item.itemImage} alt={zoomItem.item.itemName} style={zoomImg} />
              ) : (
                <div style={{ ...zoomImg, background: menuFallback(zoomItem.item.itemName, zoomItem.category).grad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 96, height: 220 }} aria-hidden>
                  {menuFallback(zoomItem.item.itemName, zoomItem.category).emoji}
                </div>
              )}
            </div>
            <div style={zoomBody}>
              {zoomItem.category && <span style={zoomCat}>{zoomItem.category}</span>}
              {(() => {
                const zi = zoomItem.item;
                const zsizes = zi.servingSizes && zi.servingSizes.length ? [...zi.servingSizes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)) : null;
                const zmin = zsizes ? Math.min(...zsizes.map((s) => s.price)) : undefined;
                return (
                  <>
                    <div style={zoomTopRow}>
                      <div style={zoomName}>{zi.itemName}</div>
                      {zsizes ? <div style={zoomPrice}>From {money(zmin!)}</div> : (typeof zi.itemPrice === 'number' && <div style={zoomPrice}>{money(zi.itemPrice)}</div>)}
                    </div>
                    {zi.itemDescription ? <div style={zoomDesc}>{zi.itemDescription}</div> : null}
                    {zi.available === false ? (
                      <div style={zoomSoldOut}>Sold out</div>
                    ) : zsizes ? (
                      <>
                        <div style={sizeListLabel}>{viewOnly ? 'Sizes' : 'Choose a size'}</div>
                        {zsizes.map((sz) => {
                          const key = `${zi.id}::${sz.id}`;
                          const sq = cart[key]?.quantity || 0;
                          return (
                            <div key={sz.id} style={sizeRow}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={sizeName}>{sz.label}</span>
                                {sz.volumeMl ? <span style={sizeMl}> · {sz.volumeMl} ml</span> : null}
                              </div>
                              <span style={sizePrice}>{money(sz.price)}</span>
                              {!viewOnly && (sq > 0 ? (
                                <div style={{ ...stepper, gap: 10 }}>
                                  <button style={stepBtn} onClick={() => decCatalogSize(zi, sz.id)} aria-label="Remove one"><Minus size={15} /></button>
                                  <span style={{ minWidth: 20, textAlign: 'center', fontWeight: 800, color: '#111827' }}>{sq}</span>
                                  <button style={stepBtn} onClick={() => addCatalogSize(zi, sz)} aria-label="Add one"><Plus size={15} /></button>
                                </div>
                              ) : (
                                <button type="button" style={sizeAddBtn} onClick={() => addCatalogSize(zi, sz)}><Plus size={14} /> Add</button>
                              ))}
                            </div>
                          );
                        })}
                        {!viewOnly && <button type="button" style={{ ...zoomDone, alignSelf: 'flex-end', marginTop: 8 }} onClick={() => setZoomItem(null)}>Done</button>}
                      </>
                    ) : viewOnly ? null : (cart[zi.id]?.quantity || 0) > 0 ? (
                      <div style={zoomStepRow}>
                        <div style={{ ...stepper, gap: 14 }}>
                          <button style={{ ...stepBtn, width: 36, height: 36 }} onClick={() => decCatalog(zi.id)}><Minus size={18} /></button>
                          <span style={{ minWidth: 26, textAlign: 'center', fontWeight: 800, fontSize: '1.15rem', color: '#111827' }}>{cart[zi.id]?.quantity || 0}</span>
                          <button style={{ ...stepBtn, width: 36, height: 36 }} onClick={() => addCatalog(zi)}><Plus size={18} /></button>
                        </div>
                        <button type="button" style={zoomDone} onClick={() => setZoomItem(null)}>Done</button>
                      </div>
                    ) : (
                      <button type="button" style={zoomAdd} onClick={() => addCatalog(zi)}><Plus size={16} /> Add to order</button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1300 };
const panel: React.CSSProperties = { background: '#fff', color: '#111827', borderTopLeftRadius: 22, borderTopRightRadius: 22, width: 'min(520px, 100vw)', height: 'min(880px, 96vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 40px rgba(0,0,0,0.28)', overflow: 'hidden' };
const sheetGrip: React.CSSProperties = { display: 'flex', justifyContent: 'center', padding: '8px 0 4px', flexShrink: 0 };
const sheetGripBar: React.CSSProperties = { width: 44, height: 5, borderRadius: 9999, background: '#e2e8f0' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px 12px', borderBottom: '1px solid #e5e7eb', gap: 8 };
const subLabel: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '0 14px 12px', background: '#f9fafb' };
const sectionTitle: React.CSSProperties = { fontSize: '0.8rem', fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 };
const orderCard: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 10px', marginBottom: 6 };
const statusBadge: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 700, borderRadius: 9999, padding: '2px 10px', whiteSpace: 'nowrap' };
const cancelBtn: React.CSSProperties = { background: '#f3f4f6', border: 'none', color: '#b91c1c', borderRadius: 8, padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' };
const reorderBtn: React.CSSProperties = { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 8, padding: '4px 10px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' };
const editBtn: React.CSSProperties = { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, padding: '4px 10px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' };
const tabBar: React.CSSProperties = { display: 'flex', gap: 4, padding: '8px 10px 0', background: '#fff', borderBottom: '1px solid #e5e7eb' };
const tabStyle = (active: boolean): React.CSSProperties => ({
  flex: 1, background: 'transparent', border: 'none', borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
  color: active ? '#111827' : '#6b7280', fontWeight: active ? 800 : 600, fontSize: '0.82rem', padding: '8px 4px 10px',
  cursor: 'pointer', whiteSpace: 'nowrap',
});
const sectionHint: React.CSSProperties = { fontSize: '0.78rem', color: '#9ca3af', margin: '-4px 0 8px' };
const emptyState: React.CSSProperties = { textAlign: 'center', color: '#6b7280', padding: '28px 12px', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.5 };
const customBox: React.CSSProperties = { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: 10 };
const customLine: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff', border: '1px solid #fde68a', borderRadius: 10, marginTop: 6 };
const customTag: React.CSSProperties = { flexShrink: 0, background: '#fef3c7', color: '#92400e', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3, borderRadius: 9999, padding: '2px 8px' };
const editBanner: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#eff6ff', color: '#1e40af', borderTop: '1px solid #bfdbfe', padding: '7px 12px', fontSize: '0.8rem', fontWeight: 600 };
const cancelEditBtn: React.CSSProperties = { marginLeft: 'auto', background: 'transparent', border: 'none', color: '#1d4ed8', fontWeight: 800, cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' };
const tabPill: React.CSSProperties = { background: '#111827', color: '#fff', borderRadius: 9999, padding: '3px 12px', fontSize: '0.8rem', fontWeight: 800 };
const searchInput: React.CSSProperties = { width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', fontSize: '0.92rem', outline: 'none', marginBottom: 8, color: '#111827', background: '#fff' };
const manualRow: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 };
const qtyInput: React.CSSProperties = { width: 54, boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 8px', fontSize: '0.92rem', textAlign: 'center', color: '#111827' };
const addManualBtn: React.CSSProperties = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, width: 40, height: 38, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const catHead: React.CSSProperties = { fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', letterSpacing: 0.2, margin: '6px 0 6px' };
const subHead: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 700, color: '#e0531f', textTransform: 'uppercase', letterSpacing: 0.4, margin: '8px 0 4px 2px' };
const itemRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff', border: '1px solid #eef2f7', borderRadius: 10, marginBottom: 6 };
// Photo-gallery menu card (Paytm-style): thumbnail + name/price + Add.
const itemCard: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: 8, background: '#fff', border: '1px solid #eef2f7', borderRadius: 14, marginBottom: 8, boxShadow: '0 1px 4px rgba(15,23,42,0.04)' };
// Premium wide card: big image on top, then name · price · full description · Add.
const itemCardWide: React.CSSProperties = { display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #eef2f7', borderRadius: 16, marginBottom: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(15,23,42,0.07)' };
const wideImgBtn: React.CSSProperties = { position: 'relative', padding: 0, border: 'none', background: '#f1f5f9', cursor: 'zoom-in', lineHeight: 0, width: '100%', display: 'block' };
const bigImg: React.CSSProperties = { width: '100%', height: 180, objectFit: 'cover', display: 'block' };
const wideZoomBadge: React.CSSProperties = { position: 'absolute', bottom: 8, right: 8, fontSize: 12, background: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: 8, padding: '2px 7px', lineHeight: 1.4 };
const soldOutOverlay: React.CSSProperties = { position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.55)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.05rem', letterSpacing: 0.5 };
const wideBody: React.CSSProperties = { padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 6 };
const wideTopRow: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 };
const wideName: React.CSSProperties = { fontSize: '1.08rem', fontWeight: 800, color: '#111827', lineHeight: 1.25, flex: 1 };
const widePrice: React.CSSProperties = { fontSize: '1.08rem', fontWeight: 800, color: '#e0531f', whiteSpace: 'nowrap' };
const wideDesc: React.CSSProperties = { fontSize: '0.88rem', color: '#6b7280', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
const wideActionRow: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', marginTop: 4 };
const addBtnWide: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 22px', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer' };
const thumbImg: React.CSSProperties = { width: 68, height: 68, borderRadius: 12, objectFit: 'cover', flexShrink: 0, display: 'block' };
const itemInfo: React.CSSProperties = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 };
const itemNameText: React.CSSProperties = { fontSize: '0.94rem', fontWeight: 700, color: '#111827', lineHeight: 1.25 };
const itemPriceText: React.CSSProperties = { fontSize: '0.9rem', fontWeight: 800, color: '#e0531f' };
const comboBadge: React.CSSProperties = { marginLeft: 6, background: '#e0531f', color: '#fff', borderRadius: 9999, padding: '1px 8px', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', verticalAlign: 'middle' };
const comboInside: React.CSSProperties = { fontSize: '0.78rem', color: '#6b7280', marginTop: 2 };
const itemDescText: React.CSSProperties = { fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.3, marginTop: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
const thumbBtn: React.CSSProperties = { position: 'relative', padding: 0, border: 'none', background: 'transparent', cursor: 'zoom-in', flexShrink: 0, lineHeight: 0, borderRadius: 12 };
const thumbZoomBadge: React.CSSProperties = { position: 'absolute', bottom: 3, right: 3, fontSize: 10, background: 'rgba(0,0,0,0.45)', borderRadius: 6, padding: '1px 3px', lineHeight: 1.3 };
// Zoom / item-detail modal
// Overlay scrolls; the card is centred with margin:auto so a tall card never clips its top
// (flex `align-items:center` would cut the top off and make it unreachable).
const zoomOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', zIndex: 1400, padding: 16, overflowY: 'auto' };
const zoomCard: React.CSSProperties = { position: 'relative', margin: 'auto', background: '#fff', borderRadius: 18, width: 'min(420px, 100%)', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' };
const zoomClose: React.CSSProperties = { position: 'absolute', top: 10, right: 10, zIndex: 2, width: 34, height: 34, borderRadius: 9999, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const zoomImgWrap: React.CSSProperties = { width: '100%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', maxHeight: '56vh', overflow: 'hidden' };
const zoomImg: React.CSSProperties = { width: '100%', maxHeight: '56vh', objectFit: 'contain', display: 'block' };
const zoomBody: React.CSSProperties = { padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 8 };
const zoomCat: React.CSSProperties = { alignSelf: 'flex-start', fontSize: '0.68rem', fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: '#e0531f', background: '#fff2ec', padding: '3px 10px', borderRadius: 9999 };
const zoomTopRow: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 };
const zoomName: React.CSSProperties = { fontSize: '1.25rem', fontWeight: 800, color: '#111827', lineHeight: 1.25, flex: 1 };
const zoomPrice: React.CSSProperties = { fontSize: '1.2rem', fontWeight: 800, color: '#e0531f', whiteSpace: 'nowrap' };
const zoomStepRow: React.CSSProperties = { marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 };
const zoomDone: React.CSSProperties = { background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: 12, padding: '11px 24px', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer' };
const zoomSoldOut: React.CSSProperties = { marginTop: 6, textAlign: 'center', background: '#f1f5f9', color: '#94a3b8', borderRadius: 12, padding: '12px', fontWeight: 800 };
const zoomDesc: React.CSSProperties = { fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.5, whiteSpace: 'pre-line' };
const zoomAdd: React.CSSProperties = { marginTop: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 16px', fontWeight: 800, cursor: 'pointer' };
const addBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(255,107,53,0.1)', color: '#e0531f', border: '1px solid rgba(255,107,53,0.35)', borderRadius: 9999, padding: '7px 14px', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0 };
const soldOutPill: React.CSSProperties = { background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 9999, padding: '7px 12px', fontWeight: 800, fontSize: '0.78rem', flexShrink: 0, whiteSpace: 'nowrap' };
const stepper: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 };
const stepBtn: React.CSSProperties = { background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };

// ── Delivery-app menu rows (Careem/Talabat style): text left, image right, floating Add ──
const searchBar: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', margin: '8px 14px 0', background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 12, flexShrink: 0 };
const searchBarInput: React.CSSProperties = { flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '0.95rem', color: '#111827', minWidth: 0 };
const searchClear: React.CSSProperties = { background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 };
const catChipBar: React.CSSProperties = { display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '9px 14px', background: '#fff', borderBottom: '1px solid #eef2f7', flexShrink: 0 };
const catChip: React.CSSProperties = { flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', color: '#374151', borderRadius: 9999, padding: '7px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const catChipActive: React.CSSProperties = { ...catChip, background: '#FF6B35', borderColor: '#FF6B35', color: '#fff' };
const menuListWrap: React.CSSProperties = { background: '#fff', border: '1px solid #eef2f7', borderRadius: 14, padding: '2px 14px', marginTop: 2 };
// Section banner (NOT sticky — a sticky header floats over the list and overlaps items; the fixed
// chip strip already shows the current category). A clean inline divider between sections.
const catHeadBig: React.CSSProperties = { background: '#fff', display: 'flex', alignItems: 'center', gap: 8, padding: '14px 0 8px', borderBottom: '2px solid #FF6B35', marginBottom: 4 };
const catEmoji: React.CSSProperties = { fontSize: 20, lineHeight: 1 };
const catTitle: React.CSSProperties = { fontSize: '1.15rem', fontWeight: 800, color: '#111827' };
const catCount: React.CSSProperties = { marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af' };
const dRow: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 0', borderBottom: '1px solid #f1f3f5' };
const dInfo: React.CSSProperties = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 2 };
const dName: React.CSSProperties = { fontSize: '1rem', fontWeight: 700, color: '#111827', lineHeight: 1.3 };
const dDesc: React.CSSProperties = { fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
const dPriceRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 };
const dPrice: React.CSSProperties = { fontSize: '0.98rem', fontWeight: 800, color: '#111827' };
const dSoldTag: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3 };
const dMedia: React.CSSProperties = { position: 'relative', width: 116, flexShrink: 0, alignSelf: 'center', paddingBottom: 16 };
const dImgBtn: React.CSSProperties = { padding: 0, border: 'none', background: 'transparent', cursor: 'zoom-in', borderRadius: 12, lineHeight: 0, width: '100%', display: 'block' };
const rowImg: React.CSSProperties = { width: 116, height: 116, borderRadius: 12, objectFit: 'cover', display: 'block' };
const dAddWrap: React.CSSProperties = { position: 'absolute', left: '50%', bottom: 2, transform: 'translateX(-50%)' };
const dAddFloat: React.CSSProperties = { background: '#fff', color: '#e0531f', border: '1px solid #ffd0b8', borderRadius: 10, padding: '8px 22px', fontWeight: 800, fontSize: '0.9rem', boxShadow: '0 6px 16px rgba(15,23,42,0.16)', cursor: 'pointer', whiteSpace: 'nowrap' };
const dStepFloat: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #ffd0b8', borderRadius: 10, boxShadow: '0 6px 16px rgba(15,23,42,0.16)', padding: '4px 8px' };
const dStepBtn: React.CSSProperties = { background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 7, width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const dStepQty: React.CSSProperties = { minWidth: 16, textAlign: 'center', fontWeight: 800, fontSize: '0.95rem', color: '#111827' };
const dSizeTag: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 800, color: '#e0531f', background: '#fff2ec', borderRadius: 9999, padding: '2px 8px', letterSpacing: 0.2 };
// Serving-size chooser rows inside the detail popup.
const sizeListLabel: React.CSSProperties = { fontSize: '0.78rem', fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, margin: '8px 0 2px' };
const sizeRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f3f5' };
const sizeName: React.CSSProperties = { fontSize: '0.98rem', fontWeight: 700, color: '#111827' };
const sizeMl: React.CSSProperties = { fontSize: '0.82rem', color: '#9ca3af' };
const sizePrice: React.CSSProperties = { fontSize: '0.95rem', fontWeight: 800, color: '#111827', whiteSpace: 'nowrap' };
const sizeAddBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', color: '#e0531f', border: '1px solid #ffd0b8', borderRadius: 10, padding: '7px 14px', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' };

/** Category-based fallback tile when a menu item has no photo — inferred from its name/category. */
const FOOD_FALLBACKS: { re: RegExp; emoji: string; grad: string }[] = [
  { re: /coffee|espresso|latte|cappuccino|\btea\b|chai|mocha/i, emoji: '☕', grad: 'linear-gradient(135deg,#c49a6c,#e6cba5)' },
  // Alcohol first (specific), so a whisky/beer/wine never falls through to the soft-drink cup.
  { re: /whisk|scotch|bourbon|\brum\b|vodka|\bgin\b|brandy|tequila|cognac|liqueur|liquor|spirit|single malt/i, emoji: '🥃', grad: 'linear-gradient(135deg,#b45309,#f59e0b)' },
  { re: /beer|lager|\bale\b|stout|pilsner|pint|\bipa\b|brew/i, emoji: '🍺', grad: 'linear-gradient(135deg,#d97706,#fcd34d)' },
  { re: /wine|merlot|chardonnay|sauvignon|shiraz|cabernet|prosecco|champagne|sangria|rosé|rose wine/i, emoji: '🍷', grad: 'linear-gradient(135deg,#7f1d1d,#be123c)' },
  { re: /cocktail|mocktail|martini|margarita|mojito|daiquiri|negroni|sangria/i, emoji: '🍸', grad: 'linear-gradient(135deg,#0ea5e9,#67e8f9)' },
  { re: /juice|water|soda|cola|coke|pepsi|sprite|fanta|lemonade|shake|smoothie|lassi|drink|beverage|milk|tonic|energy/i, emoji: '🥤', grad: 'linear-gradient(135deg,#38bdf8,#a5f3fc)' },
  { re: /cake|dessert|ice ?cream|sweet|pastry|brownie|pudding|kulfi|halwa|gulab|jamun/i, emoji: '🍰', grad: 'linear-gradient(135deg,#f9a8d4,#fbcfe8)' },
  { re: /pizza/i, emoji: '🍕', grad: 'linear-gradient(135deg,#ef4444,#fca5a5)' },
  { re: /burger|sandwich|wrap/i, emoji: '🍔', grad: 'linear-gradient(135deg,#f97316,#fdba74)' },
  { re: /biryani|\brice\b|curry|thali|meal|gravy|masala|\bdal\b|pulao/i, emoji: '🍛', grad: 'linear-gradient(135deg,#fb923c,#fed7aa)' },
  { re: /dosa|idli|vada|uttapam|breakfast|pancake|appam/i, emoji: '🥞', grad: 'linear-gradient(135deg,#fbbf24,#fde68a)' },
  { re: /roti|naan|bread|paratha|kulcha|chapati/i, emoji: '🫓', grad: 'linear-gradient(135deg,#d6a55f,#efd6a8)' },
  { re: /fries|snack|starter|chaat|samosa|pakora|\bfry\b|tikka|kebab|wings|nuggets/i, emoji: '🍟', grad: 'linear-gradient(135deg,#f59e0b,#fcd34d)' },
  { re: /soup/i, emoji: '🍜', grad: 'linear-gradient(135deg,#fb7185,#fecdd3)' },
  { re: /salad|veg|greens/i, emoji: '🥗', grad: 'linear-gradient(135deg,#22c55e,#bbf7d0)' },
  { re: /noodle|pasta|hakka|chow/i, emoji: '🍜', grad: 'linear-gradient(135deg,#f472b6,#fbcfe8)' },
];
function menuFallback(name: string, category?: string): { emoji: string; grad: string } {
  const hay = `${name || ''} ${category || ''}`;
  for (const f of FOOD_FALLBACKS) if (f.re.test(hay)) return f;
  return { emoji: '🍽️', grad: 'linear-gradient(135deg,#FF8A5B,#FFD1B8)' }; // default: brand-tinted food tile
}
/** Menu item thumbnail: the real photo if present (and loads), else a category-based emoji tile. */
const MenuThumb: React.FC<{ src?: string; name: string; category?: string; big?: boolean; row?: boolean }> = ({ src, name, category, big, row }) => {
  const [err, setErr] = useState(false);
  const box = big ? bigImg : row ? rowImg : thumbImg;
  if (src && !err) {
    return <img src={src} alt={name} loading="lazy" onError={() => setErr(true)} style={box} />;
  }
  const fb = menuFallback(name, category);
  return (
    <div style={{ ...box, background: fb.grad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: big ? 60 : row ? 44 : 30 }} aria-hidden>
      {fb.emoji}
    </div>
  );
};
const footer: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderTop: '1px solid #e5e7eb' };
const sendBtn: React.CSSProperties = { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' };
const viewMenuBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 9999, padding: '5px 12px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const errorBox: React.CSSProperties = { background: '#fef2f2', color: '#b91c1c', borderTop: '1px solid #fecaca', padding: '7px 12px', fontSize: '0.82rem' };

export default TableServiceOrderModal;
